import type { Product } from "./products";

export const extraProducts2: Product[] = [
  {
    slug: "ghrp-6",
    name: "GHRP-6",
    subtitle: "Growth Hormone Releasing Peptide-6 · Hexapeptide",
    category: "growth-hormone-secretagogues",
    rating: 4.7,
    reviews: 84,
    price: 27.99,
    sizes: [
      { label: "5 mg", price: 27.99 },
      { label: "10 mg", price: 49.99 },
    ],
    purity: "≥99% (HPLC)",
    cas: "87616-84-0",
    formula: "C46H56N12O6",
    molecularWeight: "873.0 g/mol",
    sequence: "His-D-Trp-Ala-Trp-D-Phe-Lys-NH2",
    appearance: "White lyophilised powder",
    storage:
      "Store at -20°C. After reconstitution, refrigerate at 2–8°C and use within 21 days.",
    short:
      "The original GHRP-class hexapeptide — the compound that defined ghrelin-receptor research. ≥99% purity, batch COA with every vial.",
    description: [
      "GHRP-6 is the pioneering synthetic hexapeptide of the growth-hormone-releasing peptide class. Its discovery opened the entire research field around the ghrelin receptor (GHS-R1a), and it remains the standard reference compound for comparative secretagogue pharmacology, including studies of its documented orexigenic (appetite-pathway) activity.",
      "Noxptide GHRP-6 is synthesised to ≥99% purity and independently verified by HPLC and mass spectrometry. Every order ships with a batch-specific Certificate of Analysis.",
      "Supplied lyophilised in sterile, tamper-evident vials with tracked UK delivery.",
    ],
    applications: [
      "GHS-R1a (ghrelin receptor) reference research",
      "Comparative GHRP-class pharmacology studies",
      "Appetite-pathway (orexigenic) models",
      "Secretagogue mechanism-of-action research",
    ],
    faqs: [
      {
        q: "How does GHRP-6 differ from Ipamorelin?",
        a: "Both act at GHS-R1a, but the literature characterises GHRP-6 as the older, broader-activity compound with notable appetite-pathway effects, while Ipamorelin is the most selective of the class.",
      },
      {
        q: "What purity is supplied?",
        a: "≥99% by independent HPLC with MS identity confirmation, documented on the batch COA included with every order.",
      },
      {
        q: "How should GHRP-6 be stored?",
        a: "Lyophilised at -20°C; after reconstitution refrigerate at 2–8°C and use within 21 days.",
      },
    ],
    references: [
      {
        title: "GHRP-6 research on PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=GHRP-6",
      },
      {
        title: "Growth hormone secretagogue receptor literature",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=growth+hormone+secretagogue+receptor",
      },
    ],
  },
  {
    slug: "ghrp-2",
    name: "GHRP-2",
    subtitle: "Pralmorelin · Growth Hormone Releasing Peptide-2",
    category: "growth-hormone-secretagogues",
    rating: 4.7,
    reviews: 71,
    price: 27.99,
    sizes: [
      { label: "5 mg", price: 27.99 },
      { label: "10 mg", price: 49.99 },
    ],
    purity: "≥99% (HPLC)",
    cas: "158861-67-7",
    formula: "C45H55N9O6",
    molecularWeight: "817.98 g/mol",
    sequence: "D-Ala-D-2-Nal-Ala-Trp-D-Phe-Lys-NH2",
    appearance: "White lyophilised powder",
    storage:
      "Store at -20°C. After reconstitution, refrigerate at 2–8°C and use within 21 days.",
    short:
      "Pralmorelin — the high-potency second-generation GHRP for secretagogue research. ≥99% purity, independently batch-verified.",
    description: [
      "GHRP-2 (Pralmorelin) is a second-generation synthetic hexapeptide secretagogue, documented in the literature as among the most potent of the GHRP class at the ghrelin receptor. It is widely used as a benchmark compound in comparative studies of secretagogue potency, pharmacokinetics and pituitary signalling.",
      "Our GHRP-2 is synthesised to ≥99% purity and independently verified by HPLC and mass spectrometry against its theoretical 817.98 g/mol. Every batch ships with its Certificate of Analysis.",
      "Supplied lyophilised in sterile, tamper-evident vials, dispatched tracked from our UK facility.",
    ],
    applications: [
      "Ghrelin receptor (GHS-R1a) pharmacology research",
      "Comparative secretagogue potency benchmarks",
      "Pituitary signalling pathway studies",
      "Pharmacokinetic comparison models",
    ],
    faqs: [
      {
        q: "What is the difference between GHRP-2 and GHRP-6?",
        a: "Both are hexapeptide secretagogues at GHS-R1a. The literature characterises GHRP-2 as the more potent of the two, with a somewhat different off-target profile — they are frequently used as comparative benchmarks.",
      },
      {
        q: "Is GHRP-2 the same as Pralmorelin?",
        a: "Yes — Pralmorelin is the INN (generic name) for GHRP-2.",
      },
      {
        q: "Is a COA included?",
        a: "Yes — every batch ships with its batch-specific Certificate of Analysis showing ≥99% HPLC purity and MS identity confirmation.",
      },
    ],
    references: [
      {
        title: "GHRP-2 / Pralmorelin research on PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=GHRP-2+pralmorelin",
      },
      {
        title: "Ghrelin receptor pharmacology literature",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=ghrelin+receptor+hexapeptide",
      },
    ],
  },
  {
    slug: "hgh-fragment-176-191",
    name: "HGH Fragment 176-191",
    subtitle: "hGH (176-191) · Lipolytic growth-hormone fragment",
    category: "metabolic-pigmentation",
    rating: 4.7,
    reviews: 96,
    price: 26.99,
    sizes: [
      { label: "2 mg", price: 26.99 },
      { label: "5 mg", price: 46.99 },
    ],
    purity: "≥99% (HPLC)",
    cas: "221231-10-3",
    formula: "C78H123N23O23S2",
    molecularWeight: "1815.1 g/mol",
    sequence:
      "Leu-Arg-Ile-Val-Gln-Cys-Arg-Ser-Val-Glu-Gly-Ser-Cys-Gly-Phe (unmodified fragment)",
    appearance: "White lyophilised powder",
    storage:
      "Store at -20°C. After reconstitution, refrigerate at 2–8°C and use within 21 days.",
    short:
      "The unmodified lipolytic C-terminal fragment of human growth hormone — the reference compound behind AOD-9604 research. COA-verified ≥99%.",
    description: [
      "HGH Fragment 176-191 is the unmodified C-terminal region of human growth hormone examined in the literature for its lipolytic activity without the growth-promoting effects of the full-length hormone. It is the parent sequence from which the modified analogue AOD-9604 was derived.",
      "Laboratories use the unmodified fragment as a comparator against AOD-9604 when studying how sequence modification alters stability and activity. Both are stocked at ≥99% purity, independently HPLC and MS verified, with batch-specific COAs.",
      "Supplied lyophilised in sterile vials with tracked UK delivery and same-day dispatch before 4pm.",
    ],
    applications: [
      "Lipolysis and fat-metabolism pathway research",
      "Comparator studies against AOD-9604",
      "hGH fragment structure–activity research",
      "Metabolic regulation models",
    ],
    faqs: [
      {
        q: "What is the difference between HGH Fragment 176-191 and AOD-9604?",
        a: "AOD-9604 is the modified analogue (with a tyrosine added at the N-terminus) of the native 176-191 fragment. Research programmes often run both to compare stability and activity.",
      },
      {
        q: "Does the fragment have growth activity in research models?",
        a: "The literature examines this fragment specifically because it isolates the lipolytic region without the growth-promoting activity of full-length hGH.",
      },
      {
        q: "What documentation ships with it?",
        a: "A batch-specific COA with ≥99% HPLC purity and MS identity confirmation, matching the vial batch number.",
      },
    ],
    references: [
      {
        title: "hGH fragment 176-191 research on PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=growth+hormone+fragment+176-191",
      },
      {
        title: "Lipolytic peptide fragment literature",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=AOD9604+fragment+lipolytic",
      },
    ],
  },
  {
    slug: "cjc-1295-dac",
    name: "CJC-1295 With DAC",
    subtitle: "CJC-1295 (Drug Affinity Complex) · Long-acting GHRH analogue",
    category: "growth-hormone-secretagogues",
    rating: 4.7,
    reviews: 63,
    price: 38.99,
    sizes: [
      { label: "2 mg", price: 38.99 },
      { label: "5 mg", price: 69.99 },
    ],
    purity: "≥99% (HPLC)",
    cas: "863288-34-0",
    formula: "C165H269N47O46 (conjugate)",
    molecularWeight: "3640.1 g/mol (approx., conjugate)",
    sequence: "Modified GRF 1-29 + maleimidopropionyl-lysine (DAC conjugate)",
    appearance: "White lyophilised powder",
    storage:
      "Store at -20°C. After reconstitution, refrigerate at 2–8°C and use within 14 days.",
    short:
      "The long-acting DAC-conjugated GHRH analogue for sustained-exposure endocrine research. ≥99% purity, batch-specific COA included.",
    description: [
      "CJC-1295 With DAC pairs the Modified GRF 1-29 peptide with a Drug Affinity Complex — a reactive group that binds circulating albumin and dramatically extends the compound's research half-life. Where the No-DAC form suits pulsatile protocols, the DAC form is the tool for sustained-exposure endocrine models.",
      "Because the DAC conjugate is a different molecular entity, analytical verification matters: our material is independently HPLC-verified to ≥99% purity with mass spectrometry characterisation, and every vial ships with its batch-specific COA.",
      "Supplied lyophilised in sterile, tamper-evident vials with tracked UK delivery.",
    ],
    applications: [
      "Sustained-exposure GHRH receptor research",
      "Albumin-binding conjugate pharmacology studies",
      "Comparative DAC vs No-DAC research designs",
      "Long-acting analogue pharmacokinetic models",
    ],
    faqs: [
      {
        q: "What does DAC mean in CJC-1295?",
        a: "DAC stands for Drug Affinity Complex — a conjugated group that binds albumin and greatly extends the compound's half-life in research models, unlike the short-acting No-DAC form.",
      },
      {
        q: "Should I choose DAC or No DAC?",
        a: "It depends on protocol design: No-DAC (Modified GRF 1-29) preserves pulsatile signalling; the DAC form suits sustained-exposure models. We stock both, each with batch-specific COAs.",
      },
      {
        q: "How is it stored?",
        a: "Lyophilised at -20°C; after reconstitution refrigerate at 2–8°C and use within 14 days.",
      },
    ],
    references: [
      {
        title: "CJC-1295 DAC research on PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=CJC-1295+DAC",
      },
      {
        title: "GHRH analogue pharmacokinetics literature",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=GHRH+analog+albumin+half-life",
      },
    ],
  },
  {
    slug: "igf-1-lr3",
    name: "IGF-1 LR3",
    subtitle: "Long R3 Insulin-like Growth Factor-1 · 83 amino acids",
    category: "growth-hormone-secretagogues",
    rating: 4.8,
    reviews: 89,
    price: 49.99,
    sizes: [
      { label: "1 mg", price: 49.99 },
      { label: "2 mg", price: 89.99 },
    ],
    purity: "≥99% (HPLC)",
    cas: "946870-92-4",
    formula: "C400H625N111O115S9",
    molecularWeight: "9117.6 g/mol",
    sequence:
      "83-aa IGF-1 analogue with Arg3 substitution and 13-aa N-terminal extension",
    appearance: "White lyophilised powder",
    storage:
      "Store at -20°C. Reconstitute with dilute acetic acid per protocol; refrigerate at 2–8°C and use promptly.",
    short:
      "The extended-stability IGF-1 analogue for growth-factor signalling research. Recombinant-grade, ≥99% purity, full batch documentation.",
    description: [
      "IGF-1 LR3 is an engineered analogue of insulin-like growth factor-1: an Arg3 substitution reduces binding-protein affinity and a 13-amino-acid N-terminal extension extends activity, making it the standard tool for sustained IGF signalling research in cell and tissue models.",
      "At 83 amino acids, IGF-1 LR3 is a recombinant-class protein rather than a simple synthetic peptide — and one of the most counterfeited compounds in the research market. Our material is independently verified to ≥99% purity with MS identity confirmation, and every vial ships with its batch-specific COA.",
      "Supplied lyophilised in sterile vials, dispatched tracked from our UK facility with cold-pack options available.",
    ],
    applications: [
      "IGF-1 receptor signalling research",
      "Cell proliferation and differentiation models",
      "Muscle satellite-cell research",
      "Binding-protein affinity studies",
    ],
    faqs: [
      {
        q: "What does LR3 mean?",
        a: "Long R3: an IGF-1 analogue with an arginine substitution at position 3 and a 13-residue N-terminal extension, reducing binding-protein sequestration and extending activity in research models.",
      },
      {
        q: "How should IGF-1 LR3 be reconstituted?",
        a: "The literature standard is dilute (e.g., 0.6%) acetic acid rather than plain water for stability, followed by refrigeration and prompt use. Confirm solvent choice against your assay requirements.",
      },
      {
        q: "Why is IGF-1 LR3 supplied in 1 mg vials?",
        a: "It is a large recombinant-class protein (9117.6 g/mol) used in small quantities; 1 mg vials minimise waste and freeze-thaw degradation.",
      },
    ],
    references: [
      {
        title: "IGF-1 LR3 research on PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=IGF-1+LR3",
      },
      {
        title: "IGF signalling literature",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=insulin-like+growth+factor+receptor+signaling",
      },
    ],
  },
  {
    slug: "peg-mgf",
    name: "PEG-MGF",
    subtitle: "PEGylated Mechano Growth Factor · IGF-1Ec variant",
    category: "growth-hormone-secretagogues",
    rating: 4.6,
    reviews: 41,
    price: 44.99,
    sizes: [
      { label: "2 mg", price: 44.99 },
      { label: "5 mg", price: 79.99 },
    ],
    purity: "≥99% (HPLC)",
    cas: "946870-92-4 (class)",
    formula: "C121H200N42O39 (core) + PEG",
    molecularWeight: "2948.3 g/mol (core) + PEGylation",
    sequence:
      "Tyr-Gln-Pro-Pro-Ser-Thr-Asn-Lys-Asn-Thr-Lys-Ser-Gln-Arg-Arg-Lys-Gly-Ser-Thr-Phe-Glu-Glu-His-Lys (PEGylated)",
    appearance: "White lyophilised powder",
    storage:
      "Store at -20°C. After reconstitution, refrigerate at 2–8°C and use within 21 days.",
    short:
      "The PEG-stabilised form of Mechano Growth Factor for muscle-repair signalling research. ≥99% purity, batch COA supplied.",
    description: [
      "PEG-MGF is the polyethylene-glycol-conjugated form of Mechano Growth Factor, the IGF-1Ec splice variant examined in the literature for its role in muscle repair and satellite-cell activation following mechanical damage. PEGylation protects the peptide from rapid degradation, extending its experimental window.",
      "Our PEG-MGF is synthesised to ≥99% purity and independently verified by HPLC and mass spectrometry. Every order ships with a batch-specific Certificate of Analysis.",
      "Supplied lyophilised in sterile, tamper-evident vials with tracked UK delivery.",
    ],
    applications: [
      "Muscle repair and satellite-cell activation research",
      "IGF-1Ec splice-variant signalling studies",
      "PEGylation stability research",
      "Mechanotransduction pathway models",
    ],
    faqs: [
      {
        q: "What is the difference between MGF and PEG-MGF?",
        a: "PEG-MGF is the same MGF peptide conjugated with polyethylene glycol, which protects it from degradation and extends its research window. The unmodified MGF is used where native kinetics are required.",
      },
      {
        q: "What is Mechano Growth Factor?",
        a: "A splice variant of IGF-1 (IGF-1Ec) examined in the literature for muscle repair signalling after mechanical damage.",
      },
      {
        q: "Is a COA included?",
        a: "Yes — every batch ships with its batch-specific Certificate of Analysis showing ≥99% HPLC purity and MS identity confirmation.",
      },
    ],
    references: [
      {
        title: "Mechano growth factor research on PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=mechano+growth+factor",
      },
      {
        title: "PEGylated peptide stability literature",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=PEGylation+peptide+stability",
      },
    ],
  },
  {
    slug: "mgf",
    name: "MGF",
    subtitle: "Mechano Growth Factor · IGF-1Ec splice variant",
    category: "growth-hormone-secretagogues",
    rating: 4.6,
    reviews: 37,
    price: 39.99,
    sizes: [
      { label: "2 mg", price: 39.99 },
      { label: "5 mg", price: 72.99 },
    ],
    purity: "≥99% (HPLC)",
    cas: "12020-86-9 (class)",
    formula: "C121H200N42O39",
    molecularWeight: "2948.3 g/mol",
    sequence:
      "Tyr-Gln-Pro-Pro-Ser-Thr-Asn-Lys-Asn-Thr-Lys-Ser-Gln-Arg-Arg-Lys-Gly-Ser-Thr-Phe-Glu-Glu-His-Lys",
    appearance: "White lyophilised powder",
    storage:
      "Store at -20°C. After reconstitution, refrigerate at 2–8°C and use promptly (unmodified MGF is short-lived in solution).",
    short:
      "The native IGF-1Ec splice peptide for mechanotransduction and muscle-repair research. ≥99% purity, independently batch-verified.",
    description: [
      "MGF (Mechano Growth Factor) is the IGF-1Ec splice-variant peptide expressed in response to mechanical loading, examined in the literature for its role in initiating muscle-repair signalling. The unmodified form is used where native, short-duration kinetics are required — in contrast to its PEGylated counterpart.",
      "Noxptide MGF is synthesised to ≥99% purity, independently verified by HPLC and mass spectrometry, and shipped with a batch-specific Certificate of Analysis.",
      "Supplied lyophilised in sterile vials, dispatched tracked from our UK facility.",
    ],
    applications: [
      "Mechanotransduction and load-response research",
      "Muscle-repair signalling initiation studies",
      "Comparative native vs PEGylated MGF designs",
      "IGF-1 splice-variant biology research",
    ],
    faqs: [
      {
        q: "Why use unmodified MGF rather than PEG-MGF?",
        a: "The native form is required where the research design depends on short, physiological-duration signalling; PEG-MGF is used where extended stability is needed. We stock both.",
      },
      {
        q: "How stable is MGF after reconstitution?",
        a: "Unmodified MGF is relatively short-lived in solution — refrigerate at 2–8°C and use promptly after reconstitution.",
      },
      {
        q: "What purity is supplied?",
        a: "≥99% by independent HPLC with MS identity confirmation, documented on the batch COA included with every order.",
      },
    ],
    references: [
      {
        title: "Mechano growth factor research on PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=mechano+growth+factor+IGF-1Ec",
      },
    ],
  },
  {
    slug: "thymosin-alpha-1",
    name: "Thymosin Alpha-1",
    subtitle: "Tα1 · 28-amino-acid thymic peptide",
    category: "recovery-repair",
    rating: 4.8,
    reviews: 58,
    price: 41.99,
    sizes: [
      { label: "5 mg", price: 41.99 },
      { label: "10 mg", price: 74.99 },
    ],
    purity: "≥99% (HPLC)",
    cas: "62304-98-7",
    formula: "C129H215N33O55",
    molecularWeight: "3108.3 g/mol",
    sequence:
      "Ac-Ser-Asp-Ala-Ala-Val-Asp-Thr-Ser-Ser-Glu-Ile-Thr-Thr-Lys-Asp-Leu-Lys-Glu-Lys-Lys-Glu-Val-Val-Glu-Glu-Ala-Glu-Asn-OH",
    appearance: "White lyophilised powder",
    storage:
      "Store at -20°C. After reconstitution, refrigerate at 2–8°C and use within 21 days.",
    short:
      "The clinically documented thymic peptide for immune-response research. ≥99% purity, batch-verified, COA with every vial.",
    description: [
      "Thymosin Alpha-1 is a 28-amino-acid peptide originally isolated from thymic tissue, and one of the most extensively documented immune-modulating peptides in the research literature — with studies spanning T-cell maturation, immune-response regulation and vaccine-adjuvant research models.",
      "Our Thymosin Alpha-1 is synthesised to ≥99% purity and independently verified by HPLC and mass spectrometry against its theoretical 3108.3 g/mol. Every order ships with a batch-specific Certificate of Analysis.",
      "Supplied lyophilised in sterile, tamper-evident vials with tracked UK delivery.",
    ],
    applications: [
      "T-cell maturation and immune-response research",
      "Thymic peptide signalling studies",
      "Vaccine-adjuvant research models",
      "Immunosenescence pathway research",
    ],
    faqs: [
      {
        q: "What is Thymosin Alpha-1 used for in research?",
        a: "Published research examines Thymosin Alpha-1 in T-cell maturation, immune-response regulation and adjuvant models. It is supplied strictly for laboratory research use only.",
      },
      {
        q: "How does it relate to TB-500?",
        a: "Both are discussed in thymic-peptide research contexts, but they are distinct materials. Thymosin Alpha-1 is a defined 28-amino-acid peptide; TB-500 identity should be confirmed from its current batch COA before comparison.",
      },
      {
        q: "What documentation is included?",
        a: "A batch-specific COA with ≥99% HPLC purity and MS identity confirmation ships with every order.",
      },
    ],
    references: [
      {
        title: "Thymosin Alpha-1 research on PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=thymosin+alpha-1",
      },
      {
        title: "Thymic peptides in immune research",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=thymic+peptide+immune+modulation",
      },
    ],
  },
  {
    slug: "kisspeptin-10",
    name: "Kisspeptin-10",
    subtitle: "Kp-10 · Decapeptide · Kiss1 (112-121)",
    category: "cognitive-neuropeptides",
    rating: 4.6,
    reviews: 29,
    price: 34.99,
    sizes: [
      { label: "5 mg", price: 34.99 },
      { label: "10 mg", price: 62.99 },
    ],
    purity: "≥99% (HPLC)",
    cas: "374675-21-5",
    formula: "C63H83N17O14",
    molecularWeight: "1302.4 g/mol",
    sequence: "Tyr-Asn-Trp-Asn-Ser-Phe-Gly-Leu-Arg-Phe-NH2",
    appearance: "White lyophilised powder",
    storage:
      "Store at -20°C. After reconstitution, refrigerate at 2–8°C and use within 21 days.",
    short:
      "The minimal active kisspeptin fragment for KISS1R and reproductive-axis research. ≥99% purity, batch COA included.",
    description: [
      "Kisspeptin-10 is the shortest fully active fragment of the kisspeptin family — the peptide system the literature places at the top of the reproductive endocrine cascade, signalling through the KISS1R (GPR54) receptor. It is a standard tool in hypothalamic–pituitary–gonadal axis research.",
      "Our Kisspeptin-10 is synthesised to ≥99% purity and independently verified by HPLC and mass spectrometry. Every order ships with a batch-specific Certificate of Analysis.",
      "Supplied lyophilised in sterile vials, dispatched tracked from our UK facility.",
    ],
    applications: [
      "KISS1R (GPR54) receptor signalling research",
      "Hypothalamic–pituitary–gonadal axis studies",
      "Reproductive endocrinology models",
      "Comparative kisspeptin fragment research",
    ],
    faqs: [
      {
        q: "What is Kisspeptin-10?",
        a: "The minimal fully active 10-amino-acid fragment of the kisspeptin family, used in research on KISS1R signalling and the reproductive endocrine axis. Supplied for research use only.",
      },
      {
        q: "Why the 10-mer rather than longer kisspeptins?",
        a: "Kisspeptin-10 retains full receptor activity of the longer forms while being simpler to synthesise and characterise analytically, making it the standard research tool.",
      },
      {
        q: "How is purity verified?",
        a: "By independent HPLC (≥99%) and mass spectrometry identity confirmation, documented on the batch COA supplied with your order.",
      },
    ],
    references: [
      {
        title: "Kisspeptin research on PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=kisspeptin-10",
      },
      {
        title: "KISS1R / GPR54 receptor literature",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=KISS1R+GPR54",
      },
    ],
  },
  {
    slug: "igf-1-des",
    name: "IGF-1 DES",
    subtitle: "Des(1-3) IGF-1 · Truncated 67-aa analogue",
    category: "growth-hormone-secretagogues",
    rating: 4.7,
    reviews: 44,
    price: 46.99,
    sizes: [
      { label: "1 mg", price: 46.99 },
      { label: "2 mg", price: 84.99 },
    ],
    purity: "≥99% (HPLC)",
    cas: "112603-35-7",
    formula: "C319H501N91O96S7",
    molecularWeight: "7371.4 g/mol",
    sequence: "IGF-1 lacking the N-terminal Gly-Pro-Glu tripeptide (des(1-3))",
    appearance: "White lyophilised powder",
    storage:
      "Store at -20°C. Reconstitute with dilute acetic acid per protocol; refrigerate at 2–8°C and use promptly.",
    short:
      "The truncated, binding-protein-resistant IGF-1 analogue for localised growth-factor research. ≥99% purity with full COA documentation.",
    description: [
      "IGF-1 DES (des(1-3) IGF-1) is a truncated analogue of insulin-like growth factor-1 missing the N-terminal three amino acids. The literature documents dramatically reduced binding-protein affinity as a result, making it the tool of choice for research into localised, high-availability IGF signalling.",
      "Like IGF-1 LR3, this is a recombinant-class protein where analytical verification is everything: our material is independently verified to ≥99% purity with MS identity confirmation at 7371.4 g/mol, and every vial ships with its batch-specific COA.",
      "Supplied lyophilised in sterile vials with tracked UK delivery and cold-pack options available.",
    ],
    applications: [
      "Localised IGF signalling research",
      "Binding-protein interaction studies",
      "Comparative DES vs LR3 vs native IGF-1 designs",
      "Cell growth and repair models",
    ],
    faqs: [
      {
        q: "What is the difference between IGF-1 DES and IGF-1 LR3?",
        a: "Both reduce binding-protein sequestration by different modifications: DES removes the N-terminal tripeptide (truncated, 67 aa), while LR3 adds an extension plus an Arg3 substitution (83 aa). The literature uses them for different signalling-duration profiles.",
      },
      {
        q: "How should IGF-1 DES be reconstituted?",
        a: "As with other IGF analogues, dilute acetic acid is the literature-standard solvent for stability, followed by refrigeration and prompt use.",
      },
      {
        q: "What purity is supplied?",
        a: "≥99% by independent HPLC with MS identity confirmation, documented on the batch COA included with every order.",
      },
    ],
    references: [
      {
        title: "des(1-3) IGF-1 research on PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=des(1-3)+IGF-1",
      },
      {
        title: "IGF binding protein literature",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=IGF+binding+protein+truncated",
      },
    ],
  },
];
